import { call, put, select, takeLatest } from "redux-saga/effects";

import type {
  FiWordCloudResolvedSettings,
  FiWordCloudSettings,
  FiWordCloudSettingsListResponse,
} from "@mapmycustomers/shared/types/entity/fieldInsight";
import type Organization from "@mapmycustomers/shared/types/Organization";

import {
  fromBeSettings,
  toBeSettings,
} from "@app/component/executiveSummary/util/wordCloudConfigConversion";
import findScopedSettingsRow from "@app/scene/dashboard/utils/findScopedSettingsRow";
import scopeToFilter from "@app/scene/dashboard/utils/scopeToFilter";
import { callApi } from "@app/store/api/callApi";
import { handleError } from "@app/store/errors/actions";
import { getOrganizationId } from "@app/store/iam/selectors";

import { fetchWordCloudSettings, saveWordCloudSettings } from "./actions";

export function* onFetchWordCloudSettings({ payload }: ReturnType<typeof fetchWordCloudSettings>) {
  try {
    const orgId: Organization["id"] = yield select(getOrganizationId);
    // The settings list holds only the caller's own rows, so a role preset never shows in it.
    const resolved: FiWordCloudResolvedSettings = yield callApi(
      "fetchFieldInsightsWordCloudSettingsInEffect",
      orgId,
      scopeToFilter(payload.scope)
    );
    yield call(payload.callback, fromBeSettings(resolved));
  } catch (error) {
    yield put(handleError({ error }));
    if (payload.failureCallback) {
      yield call(payload.failureCallback);
    }
  }
}

export function* onSaveWordCloudSettings({ payload }: ReturnType<typeof saveWordCloudSettings>) {
  try {
    const { config, scope } = payload;
    const orgId: Organization["id"] = yield select(getOrganizationId);
    const body = { ...toBeSettings(config), ...scopeToFilter(scope) };

    const existing: FiWordCloudSettingsListResponse = yield callApi(
      "fetchFieldInsightsWordCloudSettings",
      orgId,
      scopeToFilter(scope)
    );

    const existingRow = findScopedSettingsRow(existing.data, scope);

    let saved: FiWordCloudSettings;
    if (existingRow) {
      saved = yield callApi("updateFieldInsightsWordCloudSettings", orgId, existingRow.id, body);
    } else {
      saved = yield callApi("createFieldInsightsWordCloudSettings", orgId, body);
    }
    yield call(payload.callback, fromBeSettings(saved));
  } catch (error) {
    yield put(handleError({ error }));
    if (payload.failureCallback) {
      yield call(payload.failureCallback);
    }
  }
}

export function* wordCloudSettingsSagas() {
  yield takeLatest(fetchWordCloudSettings, onFetchWordCloudSettings);
  yield takeLatest(saveWordCloudSettings, onSaveWordCloudSettings);
}
